import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Separator } from '../../ui/separator';
import {
  MessageSquare,
  Send,
  AlertTriangle,
  Search,
  MoreVertical,
  Eye,
  Edit,
  CheckCircle,
  Archive,
  User,
  Clock,
  CalendarDays,
  Save,
  Download,
  FileText,
  Play
} from 'lucide-react';
import { projectService } from '../../../src/services/project.service';
import { authService } from '../../../src/services/auth.service';
import { userService } from '../../../src/services/user.service';
import { rfiService, RFI, RFICreate } from '../../../src/services/rfi.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { notificationService } from '../../../src/services/notification.service';
import { useConfirmDialog } from '../../ui/confirm-dialog';

// New RFI Modal Component
function NewRFIModal({ onRFICreated }: { onRFICreated?: () => void }) {
  const [formData, setFormData] = useState({
    subject: '',
    project: '',
    category: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    description: '',
    requestedBy: 'Current User',
    assignedTo: '',
    dueDate: '',
    location: '',
    drawingReference: '',
    specification: '',
    costImplication: false,
    scheduleImplication: false,
    notes: '',
    attachments: [] as string[]
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [open, setOpen] = useState(false);

  // Load projects and users when modal opens
  useEffect(() => {
    if (open) {
      loadProjects();
      loadUsers();
    }
  }, [open]);

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      console.log('RFI Modal - Projects API Response:', response);
      if (response.success && response.data) {
        const projectData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('RFI Modal - Setting projects:', projectData);
        setProjects(projectData);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      console.log('RFI Modal - Users API Response:', response);
      if (response.success && response.data) {
        const userData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('RFI Modal - Setting users:', userData);
        setUsers(userData);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    // Date validation
    if (formData.dueDate && new Date(formData.dueDate) <= new Date()) {
      newErrors.dueDate = 'Due date must be in the future';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setErrors({});

      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      // Get next RFI number
      let rfiNumber = `RFI-${new Date().getTime()}`;
      try {
        const numberResponse = await rfiService.getNextRFINumber(formData.project);
        if (numberResponse.success && numberResponse.data) {
          rfiNumber = numberResponse.data.rfi_number;
        }
      } catch (err) {
        console.warn('Could not fetch next RFI number, using timestamp:', err);
      }

      const rfiData: RFICreate = {
        organization_id: orgId,
        project_id: formData.project,
        rfi_number: rfiNumber,
        title: formData.subject,
        description: formData.description,
        assigned_to: formData.assignedTo || undefined,
        status: 'open',
        priority: formData.priority,
        due_date: formData.dueDate ? `${formData.dueDate}T00:00:00` : undefined,
        drawing_references: formData.drawingReference ? formData.drawingReference.split(',').map(s => s.trim()) : undefined,
        specification_references: formData.specification ? formData.specification.split(',').map(s => s.trim()) : undefined,
      };

      const response = await rfiService.createRFI(rfiData);

      if (response.success) {
        if (formData.assignedTo) {
          notificationService.rfiCreated(formData.assignedTo, rfiNumber, formData.project);
        }
        showSuccess({
          title: 'RFI Created',
          message: 'Request for Information created successfully!'
        });
        // Reset form
        setFormData({
          subject: '',
          project: '',
          category: '',
          priority: 'medium',
          description: '',
          requestedBy: 'Current User',
          assignedTo: '',
          dueDate: '',
          location: '',
          drawingReference: '',
          specification: '',
          costImplication: false,
          scheduleImplication: false,
          notes: '',
          attachments: []
        });
        setErrors({});
        setOpen(false);
        if (onRFICreated) onRFICreated();
      } else {
        showError({
          title: 'Failed to Create RFI',
          message: response.message || 'An error occurred. Please try again.'
        });
      }
    } catch (err: any) {
      console.error('Error creating RFI:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'An error occurred. Please try again.';
      showError({
        title: 'Failed to Create RFI',
        message: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <MessageSquare className="h-4 w-4 mr-2" />
          New RFI
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Create New RFI
          </DialogTitle>
          <DialogDescription>
            Submit a request for information with detailed project context
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic RFI Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <h4 className="font-medium">RFI Details</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rfi-subject">Subject *</Label>
              <Input
                id="rfi-subject"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                placeholder="e.g., Clarification needed on steel connection detail"
              />
              {errors.subject && <p className="text-sm text-destructive">{errors.subject}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rfi-project">Project *</Label>
                <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.project_name || project.name || 'Unnamed Project'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="rfi-category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="design-clarification">Design Clarification</SelectItem>
                    <SelectItem value="material-specification">Material Specification</SelectItem>
                    <SelectItem value="construction-method">Construction Method</SelectItem>
                    <SelectItem value="code-compliance">Code Compliance</SelectItem>
                    <SelectItem value="drawing-discrepancy">Drawing Discrepancy</SelectItem>
                    <SelectItem value="substitution-request">Substitution Request</SelectItem>
                    <SelectItem value="site-condition">Site Condition</SelectItem>
                    <SelectItem value="general-inquiry">General Inquiry</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-sm text-destructive">{errors.category}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rfi-description">Description *</Label>
              <Textarea
                id="rfi-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Provide detailed description of the information needed..."
                rows={4}
              />
              {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
            </div>
          </div>

          {/* Assignment & Timeline */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Assignment & Timeline</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assigned-to">Assigned To</Label>
                <Select value={formData.assignedTo} onValueChange={(value) => setFormData({...formData, assignedTo: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignee (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((user) => {
                      const displayName = user.full_name ||
                        (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
                      const displayText = user.job_title ? `${displayName} - ${user.job_title}` : displayName;
                      return (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {displayText}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {errors.assignedTo && <p className="text-sm text-destructive">{errors.assignedTo}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="rfi-priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value: any) => setFormData({...formData, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due-date">Due Date *</Label>
                <Input
                  id="due-date"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                />
                {errors.dueDate && <p className="text-sm text-destructive">{errors.dueDate}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="rfi-location">Location/Area</Label>
                <Input
                  id="rfi-location"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g., Building A - Level 3, Grid Line C5"
                />
              </div>
            </div>
          </div>

          {/* Reference Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Reference Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="drawing-reference">Drawing Reference</Label>
                <Input
                  id="drawing-reference"
                  value={formData.drawingReference}
                  onChange={(e) => setFormData({...formData, drawingReference: e.target.value})}
                  placeholder="e.g., A-101, S-201, Detail 5/A-105"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="specification">Specification Section</Label>
                <Input
                  id="specification"
                  value={formData.specification}
                  onChange={(e) => setFormData({...formData, specification: e.target.value})}
                  placeholder="e.g., Section 03 30 00 - Cast-in-Place Concrete"
                />
              </div>
            </div>
          </div>

          {/* Impact Assessment */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Impact Assessment</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="cost-implication"
                  checked={formData.costImplication}
                  onChange={(e) => setFormData({...formData, costImplication: e.target.checked})}
                  className="rounded border-border"
                />
                <Label htmlFor="cost-implication">Potential Cost Implication</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="schedule-implication"
                  checked={formData.scheduleImplication}
                  onChange={(e) => setFormData({...formData, scheduleImplication: e.target.checked})}
                  className="rounded border-border"
                />
                <Label htmlFor="schedule-implication">Potential Schedule Implication</Label>
              </div>
            </div>

            {(formData.costImplication || formData.scheduleImplication) && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-orange-900">Potential Impact Identified</p>
                    <p className="text-orange-700">
                      This RFI may affect {formData.costImplication ? 'project costs' : ''}
                      {formData.costImplication && formData.scheduleImplication ? ' and ' : ''}
                      {formData.scheduleImplication ? 'project schedule' : ''}.
                      Priority response recommended.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Additional Notes */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Additional Information</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rfi-notes">Additional Notes</Label>
              <Textarea
                id="rfi-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional context, background information, or special considerations..."
                rows={3}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          {errors.submit && (
            <p className="text-sm text-destructive w-full text-left mb-2">{errors.submit}</p>
          )}
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Submit RFI
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// RFI Response Modal Component
function RFIResponseModal({ rfi, trigger, onResponseSubmitted, open: externalOpen, onOpenChange }: { rfi: RFI; trigger?: React.ReactNode; onResponseSubmitted?: () => void; open?: boolean; onOpenChange?: (v: boolean) => void }) {
  const [response, setResponse] = useState('');
  const [status, setStatus] = useState<'open' | 'pending_response' | 'answered' | 'closed'>(rfi.status);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (v: boolean) => { setInternalOpen(v); onOpenChange?.(v); };

  const handleSubmitResponse = async () => {
    if (!response.trim()) {
      setError('Response is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Call the API to respond to the RFI
      const result = await rfiService.respondToRFI(rfi.rfi_id, {
        response: response.trim()
      });

      console.log('RFI Response result:', result);

      if (!result.success) {
        throw new Error(result.message || 'Failed to submit response');
      }

      // If status is different, update it
      if (status !== rfi.status) {
        await rfiService.updateRFI(rfi.rfi_id, { status });
      }

      // Close the modal
      setOpen(false);

      // Reset form
      setResponse('');
      setNotes('');
      setStatus(rfi.status);

      // Notify parent component to reload data
      if (onResponseSubmitted) {
        onResponseSubmitted();
      }
    } catch (err: any) {
      console.error('Error submitting RFI response:', err);
      setError(err.message || 'Failed to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[600px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Respond to RFI
          </DialogTitle>
          <DialogDescription>
            Provide response to RFI: {rfi.title}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Original Request</h4>
            <p className="text-sm text-muted-foreground">{rfi.description}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rfi-response">Response *</Label>
            <Textarea
              id="rfi-response"
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Provide detailed response to the request..."
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="response-status">Status</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as 'open' | 'pending_response' | 'answered' | 'closed')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_response">Pending Response</SelectItem>
                  <SelectItem value="answered">Answered</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="response-notes">Additional Notes</Label>
            <Textarea
              id="response-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes or follow-up required..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmitResponse} disabled={isSubmitting || !response.trim()}>
            {isSubmitting ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Submit Response
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// RFI View Details Modal Component
function RFIViewDetailsModal({ rfi, trigger, projects, users, open: externalOpen, onOpenChange }: { rfi: RFI; trigger?: React.ReactNode; projects: any[]; users: any[]; open?: boolean; onOpenChange?: (v: boolean) => void }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (v: boolean) => { setInternalOpen(v); onOpenChange?.(v); };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.name || project?.project_name || 'Unknown Project';
  };

  const getUserName = (userId: string | undefined | null) => {
    if (!userId) return 'Unassigned';
    const user = users.find(u => u.user_id === userId);
    if (!user) return userId;
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.first_name || user.last_name || user.full_name || user.name || user.email || userId;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical": return "text-red-600";
      case "high": return "text-orange-600";
      case "medium": return "text-yellow-600";
      case "low": return "text-green-600";
      default: return "text-gray-600";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild onClick={() => setIsOpen(true)}>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <FileText className="h-5 w-5" />
            RFI Details - {rfi.rfi_number}
          </DialogTitle>
          <DialogDescription>Complete information for this RFI</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Title and Status */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{rfi.title}</h3>
            <div className="flex items-center gap-2">
              <Badge variant={rfi.status === 'closed' ? 'outline' : rfi.status === 'answered' ? 'default' : 'secondary'}>
                {rfi.status}
              </Badge>
              <Badge variant={rfi.priority === 'critical' ? 'destructive' : 'outline'} className={getPriorityColor(rfi.priority)}>
                {rfi.priority} Priority
              </Badge>
            </div>
          </div>

          <Separator />

          {/* Description */}
          <div>
            <Label className="text-sm font-semibold mb-2 block">Description</Label>
            <p className="text-sm text-muted-foreground">{rfi.description}</p>
          </div>

          <Separator />

          {/* Project Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold">Project</Label>
              <p className="text-sm text-muted-foreground mt-1">{getProjectName(rfi.project_id)}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">RFI Number</Label>
              <p className="text-sm text-muted-foreground mt-1 font-mono">{rfi.rfi_number}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Submitted By</Label>
              <p className="text-sm text-muted-foreground mt-1">{getUserName(rfi.submitted_by)}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Assigned To</Label>
              <p className="text-sm text-muted-foreground mt-1">{getUserName(rfi.assigned_to)}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Created Date</Label>
              <p className="text-sm text-muted-foreground mt-1">{new Date(rfi.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Due Date</Label>
              <p className={`text-sm mt-1 ${rfi.due_date && new Date(rfi.due_date) < new Date() && rfi.status !== 'closed' ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                {rfi.due_date ? new Date(rfi.due_date).toLocaleDateString() : 'Not set'}
              </p>
            </div>
          </div>

          {/* References */}
          {(rfi.drawing_references && rfi.drawing_references.length > 0) && (
            <div>
              <Label className="text-sm font-semibold mb-2 block">Drawing References</Label>
              <div className="flex flex-wrap gap-2">
                {rfi.drawing_references.map((ref, idx) => (
                  <Badge key={idx} variant="outline">{ref}</Badge>
                ))}
              </div>
            </div>
          )}

          {(rfi.specification_references && rfi.specification_references.length > 0) && (
            <div>
              <Label className="text-sm font-semibold mb-2 block">Specification References</Label>
              <div className="flex flex-wrap gap-2">
                {rfi.specification_references.map((ref, idx) => (
                  <Badge key={idx} variant="outline">{ref}</Badge>
                ))}
              </div>
            </div>
          )}


          {/* Response Section */}
          {rfi.response && (
            <>
              <Separator />
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold text-green-800">Response</Label>
                  <span className="text-xs text-green-600">
                    {getUserName(rfi.responded_by)} on {rfi.responded_at ? new Date(rfi.responded_at).toLocaleDateString() : ''}
                  </span>
                </div>
                <p className="text-sm text-green-700">{rfi.response}</p>
              </div>
            </>
          )}

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// RFI Edit Modal Component
function RFIEditModal({ rfi, trigger, onUpdate, projects, users, open: externalOpen, onOpenChange }: { rfi: RFI; trigger?: React.ReactNode; onUpdate?: () => void; projects: any[]; users: any[]; open?: boolean; onOpenChange?: (v: boolean) => void }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (v: boolean) => { setInternalOpen(v); onOpenChange?.(v); };
  const [formData, setFormData] = useState({
    title: rfi.title,
    description: rfi.description,
    priority: rfi.priority,
    status: rfi.status,
    assigned_to: rfi.assigned_to || '',
    due_date: rfi.due_date ? new Date(rfi.due_date).toISOString().split('T')[0] : '',
    drawing_references: rfi.drawing_references || [],
    specification_references: rfi.specification_references || [],
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePriorityChange = (value: string) => {
    setFormData({...formData, priority: value as any});
  };

  const handleStatusChange = (value: string) => {
    setFormData({...formData, status: value as any});
  };

  const handleAssignedToChange = (value: string) => {
    setFormData({...formData, assigned_to: value === "unassigned" ? "" : value});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const updateData = {
        ...formData,
        due_date: formData.due_date || undefined,
        assigned_to: formData.assigned_to || undefined,
      };

      const response = await rfiService.updateRFI(rfi.rfi_id, updateData);

      if (response.success) {
        setIsOpen(false);
        if (onUpdate) onUpdate();
      } else {
        setErrors({ submit: response.message || 'Failed to update RFI' });
      }
    } catch (err: any) {
      console.error('Error updating RFI:', err);
      setErrors({ submit: err.message || 'Failed to update RFI' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild onClick={() => setIsOpen(true)}>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit RFI - {rfi.rfi_number}</DialogTitle>
          <DialogDescription>Update RFI information</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              autoComplete="off"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={4}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={handlePriorityChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending_response">Pending Response</SelectItem>
                  <SelectItem value="answered">Answered</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Assigned To */}
            <div className="space-y-2">
              <Label htmlFor="assigned_to">Assigned To</Label>
              <Select value={formData.assigned_to || "unassigned"} onValueChange={handleAssignedToChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Due Date */}
            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              />
            </div>
          </div>


          {errors.submit && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {errors.submit}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// RFI Actions Menu Component
function RFIActionsMenu({ rfi, onUpdate, projects, users }: { rfi: RFI; onUpdate?: () => void; projects: any[]; users: any[] }) {
  const { showConfirm, ConfirmDialog } = useConfirmDialog();
  const [viewOpen, setViewOpen] = useState(false);
  const [respondOpen, setRespondOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const handleCloseRFI = () => {
    showConfirm({
      title: 'Close RFI',
      description: `Are you sure you want to close RFI ${rfi.rfi_number}? This will mark the RFI as resolved.`,
      confirmText: 'Close',
      cancelText: 'Cancel',
      variant: 'default',
      onConfirm: async () => {
        try {
          await rfiService.updateRFI(rfi.rfi_id, { status: 'closed' });
          showSuccess({
            title: 'RFI closed',
            message: `RFI ${rfi.rfi_number} has been successfully closed`
          });
          if (onUpdate) {
            onUpdate();
          }
        } catch (err: any) {
          console.error('Error closing RFI:', err);
          showError({
            title: 'Close failed',
            message: err?.message || 'Failed to close RFI. Please try again.'
          });
        }
      }
    });
  };

  const handleReopenRFI = async () => {
    try {
      await rfiService.updateRFI(rfi.rfi_id, { status: 'open' });
      showSuccess({
        title: 'RFI reopened',
        message: `RFI ${rfi.rfi_number} has been reopened`
      });
      if (onUpdate) {
        onUpdate();
      }
    } catch (err: any) {
      console.error('Error reopening RFI:', err);
      showError({
        title: 'Reopen failed',
        message: err?.message || 'Failed to reopen RFI. Please try again.'
      });
    }
  };

  const handleDeleteRFI = () => {
    showConfirm({
      title: 'Delete RFI',
      description: `Are you sure you want to delete RFI ${rfi.rfi_number}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await rfiService.deleteRFI(rfi.rfi_id);
          showSuccess({
            title: 'RFI deleted',
            message: 'RFI has been successfully deleted'
          });
          if (onUpdate) {
            onUpdate();
          }
        } catch (err: any) {
          console.error('Error deleting RFI:', err);
          showError({
            title: 'Delete failed',
            message: err?.message || 'Failed to delete RFI. Please try again.'
          });
        }
      }
    });
  };

  const handleExportPDF = () => {
    showError({ title: 'Not available', message: 'RFI PDF export is not yet available.' });
  };

  const handleDuplicateRFI = async () => {
    try {
      // Use the backend's built-in duplicate functionality
      const newTitle = `${rfi.title} (Copy)`;
      await rfiService.duplicateRFI(rfi.rfi_id, newTitle);
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error duplicating RFI:', err);
    }
  };

  return (
    <>
      <ConfirmDialog />
      <RFIViewDetailsModal rfi={rfi} projects={projects} users={users} open={viewOpen} onOpenChange={setViewOpen} />
      <RFIResponseModal rfi={rfi} onResponseSubmitted={onUpdate} open={respondOpen} onOpenChange={setRespondOpen} />
      <RFIEditModal rfi={rfi} projects={projects} users={users} onUpdate={onUpdate} open={editOpen} onOpenChange={setEditOpen} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => setViewOpen(true)}>
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </DropdownMenuItem>
          {(rfi.status === 'open' || rfi.status === 'pending_response') && (
            <DropdownMenuItem onClick={() => setRespondOpen(true)}>
              <Send className="h-4 w-4 mr-2" />
              Respond
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setEditOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit RFI
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleExportPDF}>
          <Download className="h-4 w-4 mr-2" />
          Export PDF
        </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDuplicateRFI}>
            <FileText className="h-4 w-4 mr-2" />
            Duplicate
          </DropdownMenuItem>
          <Separator className="my-1" />
          {rfi.status === 'closed' ? (
            <DropdownMenuItem onClick={handleReopenRFI} className="text-blue-600">
              <Play className="h-4 w-4 mr-2" />
              Reopen RFI
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={handleCloseRFI}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Close RFI
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleDeleteRFI} className="text-destructive">
            <Archive className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}

export function RFIs() {
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [rfis, setRfis] = useState<RFI[]>([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load RFIs from database
  useEffect(() => {
    loadRFIs();
    loadProjects();
    loadUsers();
  }, []);

  const loadRFIs = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (projectFilter !== 'all') filters.project_id = projectFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (priorityFilter !== 'all') filters.priority = priorityFilter;
      if (searchQuery) filters.search = searchQuery;

      console.log('Loading RFIs with filters:', filters);
      const response = await rfiService.getRFIs(1, 100, filters);
      console.log('RFI API Response:', response);

      if (response.success && response.data) {
        // Handle both response structures:
        // 1. { data: { data: [...], total: N } } - paginated response
        // 2. { data: [...] } - direct array response
        const rfiData = Array.isArray(response.data)
          ? response.data
          : (response.data.data || []);

        console.log('Setting RFIs:', rfiData);
        setRfis(rfiData);
      } else {
        const errorMsg = response.message || 'Failed to load RFIs';
        console.error('API returned error:', errorMsg, response);
        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('Error loading RFIs:', err);
      setError(err.message || 'Failed to load RFIs');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      console.log('RFIs - Projects API Response:', response);
      if (response.success && response.data) {
        // Handle both response structures (same as users)
        const projectData = Array.isArray(response.data)
          ? response.data
          : (response.data.data || []);
        console.log('RFIs - Setting projects:', projectData);
        setProjects(projectData);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      console.log('RFIs - Users API Response:', response);
      if (response.success && response.data) {
        const userData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('RFIs - Setting users:', userData);
        setUsers(userData);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  // Reload when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      loadRFIs();
    }, 300); // Debounce
    return () => clearTimeout(timer);
  }, [projectFilter, statusFilter, priorityFilter, searchQuery]);


  // Helper function to check if RFI is overdue
  const isOverdue = (rfi: RFI) => {
    if (!rfi.due_date || rfi.status === 'closed' || rfi.status === 'answered') return false;
    return new Date(rfi.due_date) < new Date();
  };

  // Map database project_id to project name
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.name || project?.project_name || 'Unknown Project';
  };

  // Map user_id to user name
  const getUserName = (userId: string | undefined | null) => {
    if (!userId) return 'Unassigned';
    const user = users.find(u => u.user_id === userId);
    if (!user) return userId;

    // Build full name from first_name and last_name
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user.first_name) {
      return user.first_name;
    }
    if (user.last_name) {
      return user.last_name;
    }
    // Fallback to other fields
    return user.full_name || user.name || user.email || userId;
  };

  const getStatusBadge = (status: string, overdue?: boolean) => {
    if (overdue) return <Badge variant="destructive">Overdue</Badge>;
    switch (status) {
      case "open": return <Badge variant="secondary">Open</Badge>;
      case "pending_response": return <Badge variant="default">Pending Response</Badge>;
      case "answered": return <Badge variant="default" className="bg-green-600">Answered</Badge>;
      case "closed": return <Badge variant="outline">Closed</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "critical": return <Badge variant="destructive">Critical</Badge>;
      case "high": return <Badge className="bg-orange-600">High</Badge>;
      case "medium": return <Badge variant="secondary">Medium</Badge>;
      case "low": return <Badge variant="outline">Low</Badge>;
      default: return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const getProjectCounts = () => {
    return rfis.reduce((acc, rfi) => {
      if (!acc[rfi.project_id]) {
        acc[rfi.project_id] = { project: getProjectName(rfi.project_id), total: 0, open: 0, overdue: 0 };
      }
      acc[rfi.project_id].total++;
      if (rfi.status === 'open') acc[rfi.project_id].open++;
      if (isOverdue(rfi)) acc[rfi.project_id].overdue++;
      return acc;
    }, {} as any);
  };

  const projectCounts = getProjectCounts();

  // Calculate average response time
  const calculateAvgResponseTime = () => {
    const answeredRfis = rfis.filter(r => r.responded_at && r.created_at);
    if (answeredRfis.length === 0) return 0;

    const totalDays = answeredRfis.reduce((sum, rfi) => {
      const created = new Date(rfi.created_at);
      const responded = new Date(rfi.responded_at!);
      const days = (responded.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
      return sum + days;
    }, 0);

    return (totalDays / answeredRfis.length).toFixed(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">RFIs (Request for Information)</h2>
          <p className="text-muted-foreground">Track and manage project information requests</p>
        </div>
        <NewRFIModal onRFICreated={loadRFIs} />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-blue-600" />
              Total RFIs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{rfis.length}</div>
            <p className="text-xs text-blue-600 mt-1">Across all projects</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Open RFIs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{rfis.filter(r => r.status === 'open').length}</div>
            <p className="text-xs text-orange-600 mt-1">Awaiting response</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Overdue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{rfis.filter((r) => isOverdue(r)).length}</div>
            <p className="text-xs text-red-600 mt-1">Past due date</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-purple-600" />
              Avg Response Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{calculateAvgResponseTime()}</div>
            <p className="text-xs text-purple-600 mt-1">Days average</p>
          </CardContent>
        </Card>
      </div>

      {/* Project Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle>RFI Status by Project</CardTitle>
          <CardDescription>Overview of RFI activity across all projects</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.values(projectCounts).map((project: any, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">{project.project}</h4>
                <div className="flex items-center justify-between text-sm">
                  <span>Total: <strong>{project.total}</strong></span>
                  <span>Open: <strong>{project.open}</strong></span>
                  {project.overdue > 0 && (
                    <span className="text-red-600">Overdue: <strong>{project.overdue}</strong></span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* RFI Management */}
      <Card>
        <CardHeader>
          <CardTitle>RFI Management</CardTitle>
          <CardDescription>View and manage all project information requests</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search RFIs..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="pending_response">Pending Response</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* RFI List */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading RFIs...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-destructive">{error}</p>
              <Button onClick={loadRFIs} className="mt-4" variant="outline">Retry</Button>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-4">
              {rfis.map((rfi) => (
                <div key={rfi.rfi_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm font-medium">{rfi.rfi_number}</span>
                        <h4 className="font-medium">{rfi.title}</h4>
                        {getStatusBadge(rfi.status, isOverdue(rfi))}
                        {getPriorityBadge(rfi.priority)}
                      </div>

                      <p className="text-sm text-muted-foreground mb-3">{rfi.description}</p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Project:</span>
                          <p className="text-muted-foreground">{getProjectName(rfi.project_id)}</p>
                        </div>
                        <div>
                          <span className="font-medium">Submitted By:</span>
                          <p className="text-muted-foreground">{getUserName(rfi.submitted_by)}</p>
                        </div>
                        <div>
                          <span className="font-medium">Assigned To:</span>
                          <p className="text-muted-foreground">{getUserName(rfi.assigned_to)}</p>
                        </div>
                        <div>
                          <span className="font-medium">Created:</span>
                          <p className="text-muted-foreground">{new Date(rfi.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-2">
                        <div>
                          <span className="font-medium">Due Date:</span>
                          <p className={`text-muted-foreground ${isOverdue(rfi) ? 'text-red-600 font-medium' : ''}`}>
                            {rfi.due_date ? new Date(rfi.due_date).toLocaleDateString() : 'Not set'}
                          </p>
                        </div>
                      </div>

                      {rfi.drawing_references && rfi.drawing_references.length > 0 && (
                        <div className="mt-2">
                          <span className="font-medium text-sm">Drawing References: </span>
                          <span className="text-sm text-muted-foreground">{rfi.drawing_references.join(', ')}</span>
                        </div>
                      )}

                      {rfi.specification_references && rfi.specification_references.length > 0 && (
                        <div className="mt-1">
                          <span className="font-medium text-sm">Specification References: </span>
                          <span className="text-sm text-muted-foreground">{rfi.specification_references.join(', ')}</span>
                        </div>
                      )}

                      {rfi.response && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-green-800 text-sm">Response</span>
                            <span className="text-xs text-green-600">
                              by {getUserName(rfi.responded_by)} on {rfi.responded_at ? new Date(rfi.responded_at).toLocaleDateString() : ''}
                            </span>
                          </div>
                          <p className="text-sm text-green-700">{rfi.response}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {(rfi.status === 'open' || rfi.status === 'pending_response') && (
                        <RFIResponseModal
                          rfi={rfi}
                          onResponseSubmitted={loadRFIs}
                          trigger={
                            <Button variant="default" size="sm">
                              <Send className="h-4 w-4 mr-1" />
                              Respond
                            </Button>
                          }
                        />
                      )}
                      <RFIActionsMenu rfi={rfi} onUpdate={loadRFIs} projects={projects} users={users} />
                    </div>
                  </div>
                </div>
              ))}

              {rfis.length === 0 && !loading && !error && (
                <div className="text-center py-12">
                  <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No RFIs found</h3>
                  <p className="text-muted-foreground">
                    {searchQuery || projectFilter !== "all" || statusFilter !== "all" || priorityFilter !== "all"
                      ? "Try adjusting your search criteria or filters"
                      : "Create your first RFI to get started"
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Export all components
export { NewRFIModal, RFIResponseModal, RFIViewDetailsModal, RFIEditModal, RFIActionsMenu };
